import Compressor from 'compressorjs';

/**
 * Compresses an image using the Compressor.js library.
 * @param {File|Blob} image - The image file or blob to compress.
 * @param {number} quality - The quality of the compressed image (between 0 and 1).
 * @returns {Promise<Blob>} - A promise that resolves to the compressed image blob.
 */
export const compressImage = (image, quality) => {
  return new Promise((resolve, reject) => {
    new Compressor(image, {
      quality: quality,
      maxWidth: 1920,
      maxHeight: 1920,
      convertTypes: ['image/png', 'image/webp'],
      convertSize: 0,
      success: resolve,
      error: reject,
    });
  });
};

/**
 * Converts a Base64 string to a Blob object.
 * @param {string} base64 - The Base64 string to convert.
 * @returns {Blob} - The resulting Blob object.
 */
export const base64toBlob = (base64) => {
  try {
    const [prefix, data] = base64.split(';base64,');
    if (!prefix || !data) throw new Error('Formato Base64 inválido');
    const type = prefix.split(':')[1];
    const binary = atob(data);
    const array = new Uint8Array(binary.length).map((_, i) => binary.charCodeAt(i));
    return new Blob([array], { type });
  } catch (error) {
    console.error('Error al convertir Base64 a Blob:', error);
    throw error;
  }
};


/**
 * Converts a Blob object to a Base64 string.
 * @param {Blob} blob - The Blob object to convert.
 * @returns {Promise<string>} - A promise that resolves to the Base64 string.
 */
export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Compresses a Base64 image to the specified quality.
 * @param {string} base64 - The Base64 string of the image to compress.
 * @param {number} quality - The quality of the compressed image (between 0 and 1).
 * @returns {Promise<string>} - A promise that resolves to the compressed Base64 image string.
 */
export const compressBase64Image = async (base64, quality) => {
  try {
    const blob = base64toBlob(base64);
    const compressedBlob = await compressImage(blob, quality);
    return await blobToBase64(compressedBlob);
  } catch (error) {
    console.error('Error al comprimir la imagen:', error);
    throw error;
  }
};

/**
 * Opens a new tab to preview a Base64 image.
 * @param {string} base64 - The Base64 string of the image to preview.
 */
export const previewImage = (base64) => {
  const newTab = window.open();
  newTab.document.body.innerHTML = `<img src="${base64}" />`;
};

/**
 * Opens a new tab to preview a Base64 video.
 * @param {string} base64 - The Base64 string of the video to preview.
 */
export const previewVideo = (base64) => {
  const newTab = window.open();
  newTab.document.body.innerHTML = `<video src="${base64}" controls="true"></video>`;
};

/**
 * Rotates a Base64 image by 90 degrees.
 * @param {string} base64 - The Base64 string of the image to rotate.
 * @param {boolean} clockwise - Whether to rotate the image clockwise or counterclockwise.
 * @returns {Promise<string>} - A promise that resolves to the rotated Base64 image string.
 */
export const rotateImage = (base64, clockwise) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.height;
      canvas.height = img.width;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((clockwise ? 90 : -90) * Math.PI / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      resolve(canvas.toDataURL());
    };
    img.onerror = (error) => {
      reject(error);
    };
    img.src = base64;
  });
};
export const convertBase64 = (file) => {
	return new Promise((resolve, reject) => {
		const fileReader = new FileReader();

		// lee el archivo como base64
		fileReader.readAsDataURL(file);

		// resuelve con el resultado
		fileReader.onload = () => resolve(fileReader.result.split(',')[1]);

		// rechaza con el error
		fileReader.onerror = (error) => reject(error);
	});
};